import { IComplianceCalendarEntry } from '../services/regulation-applicability/regulation-applicability.model'

// Builds an iCalendar file from the compliance calendar so the dates land in Outlook or
// Google Calendar. All-day events; deliberately brand-neutral (white-label: no product name).

const escapeText = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')

const dateBasic = (isoDate: string): string => isoDate.slice(0, 10).replace(/-/g, '')

const nextDay = (isoDate: string): string => {
  const date = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString().slice(0, 10).replace(/-/g, '')
}

// RFC 5545 3.1: content lines longer than 75 octets must be folded with CRLF + one space.
// Split on UTF-8 byte length so multi-byte characters never straddle a fold.
const foldLine = (line: string): string[] => {
  const encoder = new TextEncoder()
  if (encoder.encode(line).length <= 75) return [line]
  const folded: string[] = []
  let current = ''
  let budget = 75
  for (const char of line) {
    const charBytes = encoder.encode(char).length
    if (encoder.encode(current).length + charBytes > budget) {
      folded.push(current)
      current = ` ${char}`
      budget = 75
    } else {
      current += char
    }
  }
  if (current) folded.push(current)
  return folded
}

export function buildComplianceCalendarIcs(entries: IComplianceCalendarEntry[]): string {
  const stamp = `${new Date().toISOString().replace(/[-:]/g, '').slice(0, 15)}Z`
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Compliance Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ]
  for (const entry of entries) {
    lines.push(
      'BEGIN:VEVENT',
      `UID:${entry.key}@compliance-calendar`,
      `DTSTAMP:${stamp}`,
      `DTSTART;VALUE=DATE:${dateBasic(entry.date)}`,
      `DTEND;VALUE=DATE:${nextDay(entry.date)}`,
      // Informational deadlines must not mark the whole day Busy in free/busy views.
      'TRANSP:TRANSPARENT',
      `SUMMARY:${escapeText(entry.label)}`,
      ...(entry.regulationShortLabel ? [`CATEGORIES:${escapeText(entry.regulationShortLabel)}`] : []),
      'END:VEVENT',
    )
  }
  lines.push('END:VCALENDAR')
  // RFC 5545 wants CRLF line endings and folded long lines.
  return lines.flatMap(foldLine).join('\r\n') + '\r\n'
}

export function downloadComplianceCalendarIcs(entries: IComplianceCalendarEntry[]): void {
  const blob = new Blob([buildComplianceCalendarIcs(entries)], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = 'compliance-calendar.ics'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
